/* ════════════ THE RELAY ════════════
   Client-side router: internal navigation swaps the page in place —
   no full reload. Fetches the next page's HTML, cross-fades via the
   View Transitions API, re-runs that page's script, and prefetches
   on hover so most jumps feel instant.

   Every page entry script registers its setup through onPage(init).
   The init receives an AbortSignal that fires when the user navigates
   away — tie window listeners and rAF loops to it so the old page's
   atmosphere dies with the old DOM.

   State lives on `window` because production builds bundle a copy of
   this module into every page chunk; all copies must share one router. */

type PageInit = (signal: AbortSignal) => void;

interface Nav {
  gen: number;
  ctl: AbortController;
  collecting: string | null;
  inits: Map<string, PageInit[]>;
  cache: Map<string, Promise<string>>;
  onPage(fn: PageInit): void;
}

declare global {
  interface Window {
    __cbrgNav?: Nav;
  }
}

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

/* internal routes look like "/" or "/some-page" — no dots, no .well-known */
const isRoute = (path: string) => /^\/[a-z0-9-]*$/.test(path);

function createNav(): Nav {
  const nav: Nav = {
    gen: 0,
    ctl: new AbortController(),
    collecting: null,
    inits: new Map(),
    cache: new Map(),
    onPage(fn) {
      const key = nav.collecting ?? entryScript();
      if (key) {
        const list = nav.inits.get(key) ?? [];
        list.push(fn);
        nav.inits.set(key, list);
      }
      fn(nav.ctl.signal);
    },
  };

  /* the <script type=module> of the page currently executing (initial load) */
  const entryScript = () =>
    document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? null;

  const fetchPage = (path: string): Promise<string> => {
    let p = nav.cache.get(path);
    if (!p) {
      p = fetch(path).then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.text();
      });
      p.catch(() => nav.cache.delete(path));
      nav.cache.set(path, p);
      if (nav.cache.size > 24) nav.cache.delete(nav.cache.keys().next().value!);
    }
    return p;
  };

  /* append any stylesheets the next page needs and wait until they load,
     so the body swap never paints unstyled */
  const ensureStyles = (doc: Document) => {
    const have = new Set(
      [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')].map((l) => l.href),
    );
    const wanted: string[] = [];
    const loads: Promise<void>[] = [];
    for (const l of doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')) {
      wanted.push(l.href);
      if (have.has(l.href)) continue;
      loads.push(
        new Promise((res) => {
          const el = document.createElement("link");
          el.rel = "stylesheet";
          el.href = l.href;
          el.onload = el.onerror = () => res();
          document.head.appendChild(el);
        }),
      );
    }
    return { wanted: new Set(wanted), done: Promise.all(loads) };
  };

  const swapHead = (doc: Document, keep: Set<string>) => {
    document.title = doc.title;
    for (const name of ["theme-color"]) {
      const from = doc.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      const to = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (from && to) to.content = from.content;
    }
    const fav = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const cur = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (fav && cur) cur.href = fav.href;
    for (const l of document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')) {
      if (!keep.has(l.href)) l.remove();
    }
  };

  const runScripts = async (doc: Document, base: URL, signal: AbortSignal) => {
    for (const s of doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
      const abs = new URL(s.getAttribute("src")!, base).href;
      const known = nav.inits.get(abs);
      if (known) {
        for (const fn of known) fn(signal);
      } else {
        nav.collecting = abs;
        try {
          await import(abs);
        } finally {
          nav.collecting = null;
        }
      }
    }
  };

  const navigate = async (href: string, opts: { push?: boolean; y?: number } = {}) => {
    const url = new URL(href, location.href);
    const myGen = ++nav.gen;
    document.documentElement.classList.add("nav-loading");
    let doc: Document;
    try {
      const html = await fetchPage(url.pathname);
      if (myGen !== nav.gen) return;
      doc = new DOMParser().parseFromString(html, "text/html");
      const { wanted, done } = ensureStyles(doc);
      await done;
      if (myGen !== nav.gen) return;
      nav.cache.delete(url.pathname);

      history.replaceState({ y: scrollY }, "", location.href);
      if (opts.push !== false) history.pushState({ y: 0 }, "", url.pathname + url.hash);

      nav.ctl.abort();
      nav.ctl = new AbortController();
      const signal = nav.ctl.signal;

      const apply = () => {
        swapHead(doc, wanted);
        document.body.className = doc.body.className;
        document.body.replaceChildren(...[...doc.body.children].map((n) => document.adoptNode(n)));
        const target = url.hash && document.querySelector(url.hash);
        if (target) target.scrollIntoView({ behavior: "instant", block: "start" });
        else scrollTo({ top: opts.y ?? 0, behavior: "instant" });
      };

      if (!REDUCED && document.startViewTransition) {
        const t = document.startViewTransition(apply);
        await t.updateCallbackDone;
      } else {
        apply();
      }

      await runScripts(doc, url, signal);
      if (myGen === nav.gen) bootPage(signal);
    } catch (e) {
      console.error("router: falling back to full load", e);
      location.href = url.href;
      return;
    } finally {
      if (myGen === nav.gen) document.documentElement.classList.remove("nav-loading");
    }
  };

  /* ───────── per-page chrome: scrollspy, progress, index, back-to-top ───────── */

  const SITEMAP: [string, [string, string][]][] = [
    ["the argument", [
      ["/privacy", "privacy"],
      ["/manifesto", "manifesto"],
      ["/state", "the state"],
      ["/corps", "the corps"],
      ["/now", "start now"],
      ["/you", "it starts with you"],
      ["/start", "start here"],
    ]],
    ["the dragnet", [
      ["/snowden", "snowden"],
      ["/prism", "prism"],
      ["/five-eyes", "five eyes"],
      ["/fallout", "fallout"],
    ]],
    ["agencies", [
      ["/nsa", "nsa"],
      ["/cia", "cia"],
      ["/mossad", "mossad"],
    ]],
    ["corporations", [
      ["/google", "google"],
      ["/facebook", "facebook"],
      ["/microsoft", "microsoft"],
      ["/amazon", "amazon"],
      ["/bigtech", "big tech"],
    ]],
    ["the stack", [
      ["/stack", "the stack"],
      ["/nostr", "nostr"],
      ["/bitcoin", "bitcoin"],
      ["/monero", "monero"],
      ["/tor", "tor"],
      ["/linux", "linux"],
    ]],
    ["projects", [
      ["/marmot", "marmot"],
      ["/eve", "eve"],
      ["/fastr", "fastr"],
      ["/sakura", "sakura"],
    ]],
    ["code", [
      ["/langs", "tier list"],
      ["/deps", "deps"],
      ["/odin", "odin"],
      ["/rust", "rust"],
      ["/cpp", "c++"],
      ["/ts", "typescript"],
      ["/python", "python"],
    ]],
  ];

  const injectIndex = () => {
    const links = document.querySelector(".nav-links");
    if (!links || links.querySelector(".nav-index")) return;
    const drop = document.createElement("div");
    drop.className = "nav-drop nav-index";
    const here = location.pathname;
    drop.innerHTML =
      `<button type="button" aria-haspopup="true">INDEX<span class="drop-caret" aria-hidden="true">▾</span></button>` +
      `<div class="drop-menu"><div class="drop-box index-box">` +
      SITEMAP.map(
        ([group, items]) =>
          `<div class="index-group"><span class="index-head">// ${group}</span>` +
          items
            .map(
              ([path, label]) =>
                `<a href="${path}"${path === here ? ' class="here" aria-current="page"' : ""}>${label}</a>`,
            )
            .join("") +
          `</div>`,
      ).join("") +
      `</div></div>`;
    links.appendChild(drop);
  };

  const injectMenuToggle = (signal: AbortSignal) => {
    const nav = document.querySelector(".nav");
    const links = nav?.querySelector<HTMLElement>(".nav-links");
    if (!nav || !links || nav.querySelector(".nav-toggle")) return;
    links.id ||= "nav-links";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "nav-toggle";
    btn.setAttribute("aria-label", "menu");
    btn.setAttribute("aria-expanded", "false");
    btn.setAttribute("aria-controls", links.id);
    btn.innerHTML = `<span class="bar"></span><span class="bar"></span><span class="bar"></span>`;
    btn.addEventListener(
      "click",
      () => btn.setAttribute("aria-expanded", nav.classList.toggle("menu-open") ? "true" : "false"),
      { signal },
    );
    /* tapping a destination link collapses the panel */
    links.addEventListener(
      "click",
      (e) => {
        if (!(e.target as Element).closest("a")) return;
        nav.classList.remove("menu-open");
        btn.setAttribute("aria-expanded", "false");
      },
      { signal },
    );
    nav.appendChild(btn);
  };

  const wireDropdowns = (signal: AbortSignal) => {
    /* hover works on desktop via CSS; tap-to-toggle for touch */
    document.addEventListener(
      "click",
      (e) => {
        const btn = (e.target as Element).closest?.(".nav-drop > button");
        for (const d of document.querySelectorAll(".nav-drop.open")) {
          if (!btn || d !== btn.parentElement) d.classList.remove("open");
        }
        if (btn) btn.parentElement!.classList.toggle("open");
      },
      { signal },
    );
  };

  const scrollSpy = (signal: AbortSignal) => {
    const anchors = [...document.querySelectorAll<HTMLAnchorElement>('.nav-links a[href^="#"]')];
    if (!anchors.length) return;
    const byId = new Map(anchors.map((a) => [a.hash.slice(1), a]));
    const targets = [...byId.keys()].map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (!targets.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (!en.isIntersecting) continue;
          for (const a of anchors) a.classList.toggle("active", a === byId.get(en.target.id));
        }
      },
      { rootMargin: "-25% 0px -60% 0px" },
    );
    for (const t of targets) io.observe(t);
    signal.addEventListener("abort", () => io.disconnect());
  };

  const readingProgress = (signal: AbortSignal) => {
    const update = () => {
      const max = document.documentElement.scrollHeight - innerHeight;
      const p = max > 0 ? Math.min(1, scrollY / max) : 0;
      document.documentElement.style.setProperty("--readp", p.toFixed(4));
    };
    update();
    addEventListener("scroll", update, { passive: true, signal });
    addEventListener("resize", update, { passive: true, signal });
  };

  const backToTop = (signal: AbortSignal) => {
    if (document.querySelector(".to-top")) return;
    const btn = document.createElement("button");
    btn.className = "to-top";
    btn.type = "button";
    btn.setAttribute("aria-label", "back to top");
    btn.innerHTML = "▲<span>TOP</span>";
    btn.addEventListener("click", () => scrollTo({ top: 0, behavior: REDUCED ? "instant" : "smooth" }));
    document.body.appendChild(btn);
    const toggle = () => btn.classList.toggle("show", scrollY > 600);
    toggle();
    addEventListener("scroll", toggle, { passive: true, signal });
  };

  const bootPage = (signal: AbortSignal) => {
    injectIndex();
    injectMenuToggle(signal);
    wireDropdowns(signal);
    scrollSpy(signal);
    readingProgress(signal);
    backToTop(signal);
  };

  /* ───────── global listeners (live for the whole session) ───────── */

  history.scrollRestoration = "manual";

  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;
    const a = (e.target as Element).closest?.("a[href]");
    if (!a || a.getAttribute("target") || a.hasAttribute("download")) return;
    const url = new URL((a as HTMLAnchorElement).href, location.href);
    if (url.origin !== location.origin || !isRoute(url.pathname)) return;
    if (url.pathname === location.pathname) {
      if (url.hash) return; // same-page anchor: native smooth scroll
      e.preventDefault();
      scrollTo({ top: 0, behavior: REDUCED ? "instant" : "smooth" });
      history.replaceState(history.state, "", url.pathname);
      return;
    }
    e.preventDefault();
    void navigate(url.href);
  });

  /* prefetch on intent */
  const prefetch = (e: Event) => {
    const a = (e.target as Element).closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || !isRoute(url.pathname)) return;
    if (url.pathname === location.pathname) return;
    fetchPage(url.pathname).catch(() => {});
  };
  document.addEventListener("mouseover", prefetch, { passive: true });
  document.addEventListener("touchstart", prefetch, { passive: true });

  addEventListener("popstate", (e) => {
    void navigate(location.href, { push: false, y: (e.state as { y?: number } | null)?.y ?? 0 });
  });

  /* chrome for the very first page */
  bootPage(nav.ctl.signal);

  return nav;
}

if (!window.__cbrgNav) window.__cbrgNav = createNav();

/** Register a page's setup. Runs immediately, and again on every SPA
 *  navigation back to this page. Tie listeners + rAF loops to `signal`. */
export function onPage(fn: PageInit): void {
  window.__cbrgNav!.onPage(fn);
}
